import React, { useState } from 'react';
import logger from '../utils/logger';
import Tooltip from './Tooltip';
import OccupationMultiSelect from './OccupationMultiSelect';
import LocationMultiSelect from './LocationMultiSelect';
import { US_STATES } from '../data/usLocations';
import './SearchFilters.css';

/**
 * Reusable Search Filters Component
 * Used in both SearchPage2 (main search) and EditSavedSearchModal
 * 
 * @param {Object} searchCriteria - Current search criteria values
 * @param {Function} handleInputChange - Handles input field changes
 * @param {Boolean} showAdvancedFilters - Controls advanced filters visibility
 * @param {Function} setShowAdvancedFilters - Toggles advanced filters
 * @param {Function} onSearch - Callback when Search button clicked
 * @param {Function} onNewMe - Callback when New Me button clicked
 * @param {Function} onClear - Callback when Clear Search button clicked
 * @param {Function} onSave - Callback when Save Search button clicked
 * @param {Object} currentUserProfile - Current user's profile data
 * @param {Array} bodyTypeOptions - Available body type options
 * @param {Array} occupationOptions - Available occupation options
 * @param {Array} locationOptions - Available location options
 * @param {Array} eatingOptions - Available eating preference options
 * @param {Array} lifestyleOptions - Available lifestyle options (drinking, smoking)
 * @param {Boolean} hideActionButtons - Optional: hide Save/Search buttons (for modal use)
 * @param {String} searchButtonText - Optional: custom text for search button (default: "🔍 Search")
 * @param {String} saveButtonText - Optional: custom text for save button (default: "💾 Save Search")
 * @param {Boolean} isAdmin - Whether user is admin (affects Clear vs Reset button label)
 */
const SearchFilters = ({
  searchCriteria,
  handleInputChange,
  showAdvancedFilters,
  setShowAdvancedFilters,
  onSearch,
  onNewMe,
  onClear,
  onSave,
  currentUserProfile,
  bodyTypeOptions = [],
  occupationOptions = [],
  locationOptions = [],
  eatingOptions = [],
  lifestyleOptions = [],
  hideActionButtons = false,
  searchButtonText = 'Search',
  saveButtonText = 'Save',
  isAdmin = false,
  isNearMeLoading = false
}) => {
  // Validation error state
  const [validationError, setValidationError] = useState('');
  
  // Handle Profile ID search - independent direct lookup (ignores other filters on backend)
  const handleProfileIdSearch = () => {
    if (searchCriteria.profileId?.trim()) {
      // Profile ID search is handled specially by backend - ignores other filters
      logger.debug('🔍 Direct Profile ID lookup:', searchCriteria.profileId);
      onSearch();
    } else {
      logger.debug('🔍 Profile ID is empty, not searching');
    }
  };

  // Validate main search criteria (requires at least age range for non-admin users)
  const validateMainSearch = () => {
    // Admin users can bypass all validation for troubleshooting
    if (isAdmin) {
      return { valid: true };
    }
    
    const ageMin = Number(searchCriteria.ageMin) || 0;
    const ageMax = Number(searchCriteria.ageMax) || 0;
    
    // Main search requires at least age range for non-admin users
    if (!ageMin || !ageMax) {
      return {
        valid: false,
        message: 'Please specify Age Range (Min and Max) to search'
      };
    }
    
    // Age range sanity: min must not exceed max
    if (ageMin > ageMax) {
      return {
        valid: false,
        message: `Age Min (${ageMin}) cannot be greater than Age Max (${ageMax})`
      };
    }
    
    // Height range sanity: min must not exceed max
    const hMinFeet = Number(searchCriteria.heightMinFeet) || 0;
    const hMinInches = Number(searchCriteria.heightMinInches) || 0;
    const hMaxFeet = Number(searchCriteria.heightMaxFeet) || 0;
    const hMaxInches = Number(searchCriteria.heightMaxInches) || 0;
    
    if (hMinFeet || hMinInches || hMaxFeet || hMaxInches) {
      const heightMinTotal = hMinFeet * 12 + hMinInches;
      const heightMaxTotal = hMaxFeet * 12 + hMaxInches;
      if (heightMinTotal > 0 && heightMaxTotal > 0 && heightMinTotal > heightMaxTotal) {
        return {
          valid: false,
          message: `Height Min (${hMinFeet}'${hMinInches}") cannot be greater than Height Max (${hMaxFeet}'${hMaxInches}")`
        };
      }
    }
    
    return { valid: true };
  };

  // Handle main search with validation - clears profileId to use filter criteria
  const handleMainSearch = () => {
    if (searchCriteria.profileId?.trim()) {
      setValidationError('');
      handleProfileIdSearch();
      return;
    }

    const validation = validateMainSearch();
    if (!validation.valid) {
      setValidationError(validation.message);
      // Auto-clear error after 5 seconds
      setTimeout(() => setValidationError(''), 5000);
      return;
    }
    setValidationError(''); // Clear any previous error

    onSearch();
  };

  const daysBackRawValue = searchCriteria.daysBack;
  const allowedDaysBackValues = new Set(['45', '60', '90', '365', '0']);
  const daysBackSelectValue =
    daysBackRawValue === '' || daysBackRawValue === null || daysBackRawValue === undefined
      ? '0'
      : String(daysBackRawValue);
  const showCustomDaysBackOption = daysBackSelectValue !== '' && !allowedDaysBackValues.has(daysBackSelectValue);

  const [showLocation, setShowLocation] = useState(() => Boolean((searchCriteria.locations || []).length || searchCriteria.state));
  const isDirectLookupMode = Boolean(searchCriteria.profileId && String(searchCriteria.profileId).trim());
  const roleLower = currentUserProfile?.role?.toLowerCase();
  const isAdminOrModerator = roleLower === 'admin' || roleLower === 'moderator';
  const genderDisabledTitle = isDirectLookupMode
    ? 'Disabled while searching by Profile ID / Username'
    : (!isAdminOrModerator ? 'Gender filter is locked to opposite gender' : '');

  const CHIP_DEFS = [
    { names: ['profileId'], label: c => `ID: ${c.profileId}`, when: c => !!c.profileId },
    { names: ['ageMin','ageMax'], label: c => `Age ${c.ageMin || '…'}–${c.ageMax || '…'}`, when: c => c.ageMin || c.ageMax },
    { names: ['heightMinFeet','heightMinInches','heightMaxFeet','heightMaxInches'], label: c => `Ht ${c.heightMinFeet || '…'}'${c.heightMinInches || 0}–${c.heightMaxFeet || '…'}'${c.heightMaxInches || 0}`, when: c => c.heightMinFeet || c.heightMaxFeet },
    { names: ['gender'], label: c => `Gender: ${c.gender}`, when: c => !!c.gender },
    { names: ['daysBack'], label: c => `Days back: ${c.daysBack}`, when: c => c.daysBack && String(c.daysBack) !== '0' },
    { names: ['occupations'], label: c => `Occupation ×${c.occupations.length}`, when: c => Array.isArray(c.occupations) && c.occupations.length > 0, arr: true },
    { names: ['locations'], label: c => `📍 ${c.locations[0]}${c.locations.length > 1 ? ` +${c.locations.length - 1}` : ''}`, when: c => Array.isArray(c.locations) && c.locations.length > 0, arr: true },
    { names: ['state'], label: c => `State: ${c.state}`, when: c => !!c.state },
    { names: ['bodyType'], label: c => `Body: ${c.bodyType}`, when: c => !!c.bodyType },
    { names: ['eatingPreference'], label: c => `Eating: ${c.eatingPreference}`, when: c => !!c.eatingPreference },
    { names: ['drinking'], label: c => `Drinking: ${c.drinking}`, when: c => !!c.drinking },
    { names: ['smoking'], label: c => `Smoking: ${c.smoking}`, when: c => !!c.smoking },
    { names: ['keyword'], label: c => `"${c.keyword}"`, when: c => !!c.keyword },
  ];
  const activeChips = CHIP_DEFS.filter(d => d.when(searchCriteria || {}));
  const clearChip = (def) => def.names.forEach(n => handleInputChange({ target: { name: n, value: def.arr ? [] : '' } }));
  const ADVANCED_KEYS = ['bodyType','eatingPreference','drinking','smoking','profileId','keyword'];
  const advActiveCount = ADVANCED_KEYS.filter(k => {
    const v = searchCriteria?.[k];
    return Array.isArray(v) ? v.length > 0 : Boolean(v);
  }).length;

  return (
    <div className="unified-search-filters">
      {/* Active-filter chips — one-tap remove */}
      {activeChips.length > 0 && (
        <div className="sf-chips" role="list" aria-label="Active filters">
          {activeChips.map((def, i) => (
            <span key={i} className="sf-chip" role="listitem">
              {def.label(searchCriteria)}
              <button type="button" className="sf-chip-x" aria-label="Remove filter" onClick={() => clearChip(def)}>×</button>
            </span>
          ))}
        </div>
      )}

      {/* 2. BASIC FILTERS - Always Visible */}
      <div className="filter-group">
        <div className="filter-group-head">
          <span className="fg-icon">👤</span>
          <span className="fg-title">Basics</span>
          <span className="fg-sum">Gender · Age · Height · Days back</span>
        </div>
      <div className="basic-filters-section">
        <div className="col-gender">
                        <div className="form-group">
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            Gender
                            <Tooltip 
                              text="Filter by gender preference. Default is set to opposite gender for most users. Admins can search all genders."
                              position="top"
                              icon 
                            />
                          </label>
                          <select
                            className="form-control"
                            name="gender"
                            value={searchCriteria.gender || ''}
                            onChange={handleInputChange}
                            readOnly={!isAdminOrModerator || isDirectLookupMode}
                            disabled={!isAdminOrModerator || isDirectLookupMode}
                            style={{
                              cursor: (!isAdminOrModerator || isDirectLookupMode) ? 'not-allowed' : 'pointer',
                              backgroundColor: (!isAdminOrModerator || isDirectLookupMode) ? 'var(--disabled-bg, #f0f0f0)' : 'var(--input-bg, rgba(255, 255, 255, 0.9))'
                            }}
                            title={(!isAdminOrModerator || isDirectLookupMode) ? genderDisabledTitle : ''}
                          >
                            {isAdminOrModerator && <option value="">All</option>}
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                      </div>


        <div className="col-age-range">
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              Age Range
              <Tooltip 
                text="Set minimum and maximum age for matches. Leave blank for no age restriction. Minimum allowed: 19, Maximum: 100."
                position="top"
                icon 
              />
            </label>
            <div className="age-range-inputs">
              <input
                type="number"
                className="form-control age-range-input"
                name="ageMin"
                value={searchCriteria.ageMin || ''}
                onChange={handleInputChange}
                disabled={isDirectLookupMode}
                onBlur={(e) => {
                  const value = parseInt(e.target.value);
                  if (e.target.value && value < 19) {
                    e.target.value = 19;
                    handleInputChange(e);
                  } else if (e.target.value && value > 100) {
                    e.target.value = 100;
                    handleInputChange(e);
                  }
                }}
                min="19"
                max="100"
                placeholder="Min"
                title="Minimum age: 19 | Maximum age: 100"
              />
              <input
                type="number"
                className="form-control age-range-input"
                name="ageMax"
                value={searchCriteria.ageMax || ''}
                onChange={handleInputChange}
                disabled={isDirectLookupMode}
                onBlur={(e) => {
                  const value = parseInt(e.target.value);
                  if (e.target.value && value < 19) {
                    e.target.value = 19;
                    handleInputChange(e);
                  } else if (e.target.value && value > 100) {
                    e.target.value = 100;
                    handleInputChange(e);
                  }
                }}
                min="19"
                max="100"
                placeholder="Max"
                title="Minimum age: 19 | Maximum age: 100"
              />
            </div>
          </div>
        </div>
        
        <div className="col-height-min">
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              Height (Min)
              <Tooltip 
                text="Minimum height preference. Matches will be at least this tall."
                position="top"
                icon 
              />
            </label>
            <div className="height-inputs">
              <select
                className="form-control height-select"
                name="heightMinFeet"
                value={searchCriteria.heightMinFeet || ''}
                onChange={handleInputChange}
                disabled={isDirectLookupMode}
              >
                <option value="">ft</option>
                <option value="4">4</option>
                <option value="5">5</option>
                <option value="6">6</option>
                <option value="7">7</option>
              </select>
              <select
                className="form-control height-select"
                name="heightMinInches"
                value={searchCriteria.heightMinInches || ''}
                onChange={handleInputChange}
                disabled={isDirectLookupMode}
              >
                <option value="">in</option>
                {[...Array(12)].map((_, i) => (
                  <option key={i} value={i}>{i}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="col-height-max">
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              Height (Max)
              <Tooltip 
                text="Maximum height preference. Matches will be at most this tall."
                position="top"
                icon 
              />
            </label>
            <div className="height-inputs">
              <select
                className="form-control height-select"
                name="heightMaxFeet"
                value={searchCriteria.heightMaxFeet || ''}
                onChange={handleInputChange}
                disabled={isDirectLookupMode}
              >
                <option value="">ft</option>
                <option value="4">4</option>
                <option value="5">5</option>
                <option value="6">6</option>
                <option value="7">7</option>
              </select>
              <select
                className="form-control height-select"
                name="heightMaxInches"
                value={searchCriteria.heightMaxInches || ''}
                onChange={handleInputChange}
                disabled={isDirectLookupMode}
              >
                <option value="">in</option>
                {[...Array(12)].map((_, i) => (
                  <option key={i} value={i}>{i}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="col-days-back">
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              Days Back
              <Tooltip 
                text="Show only profiles created within the last X days. Useful for finding new members."
                position="top"
                icon 
              />
            </label>
            <select
              className="form-control"
              name="daysBack"
              value={daysBackSelectValue}
              onChange={handleInputChange}
              disabled={isDirectLookupMode}
            >
              {showCustomDaysBackOption && (
                <option value={daysBackSelectValue}>{`${daysBackSelectValue} (Custom)`}</option>
              )}
              <option value="45">45</option>
              <option value="60">60</option>
              <option value="90">90</option>
              <option value="365">365</option>
              <option value="0">ALL</option>
            </select>
          </div>
        </div>

        
        

        {/* 3. ACTION BUTTONS - First appearance (after basic filters) */}
        {!hideActionButtons && !showAdvancedFilters && (
          <div className="search-action-buttons search-action-buttons-inline">
            {onSearch && (
              <button
                type="button"
                onClick={handleMainSearch}
                className="btn btn-primary"
                aria-label={searchButtonText}
              >
                <span className="btn-icon" aria-hidden="true">🔍</span>
                <span className="btn-label">{searchButtonText}</span>
              </button>
            )}
            {onNewMe && (
              <button
                type="button"
                onClick={onNewMe}
                className="btn btn-secondary"
                title="Use your current city and run search"
                aria-label="Near Me"
                disabled={isNearMeLoading}
              >
                <span className="btn-icon" aria-hidden="true">{isNearMeLoading ? '⏳' : '📍'}</span>
                <span className="btn-label">{isNearMeLoading ? 'Finding...' : 'Near Me'}</span>
              </button>
            )}
            {onClear && (
              <button
                type="button"
                onClick={onClear}
                className="btn btn-clear"
                title={isAdmin ? 'Clear all filters (widest search)' : 'Reset to your partner preference defaults'}
                aria-label={isAdmin ? 'Clear' : 'Reset'}
              >
                <span className="btn-icon" aria-hidden="true">{isAdmin ? '🗑️' : '🔄'}</span>
                <span className="btn-label">{isAdmin ? 'Clear' : 'Reset'}</span>
              </button>
            )}
            {onSave && (
              <button
                type="button"
                onClick={onSave}
                className="btn btn-secondary"
                aria-label={saveButtonText}
              >
                <span className="btn-icon" aria-hidden="true">💾</span>
                <span className="btn-label">{saveButtonText}</span>
              </button>
            )}
          </div>
        )}
      
      </div>
      
      {/* Validation Error Message */}
      {validationError && (
        <div className="search-validation-error" style={{
          background: 'rgba(231, 76, 60, 0.1)',
          border: '1px solid var(--danger-color)',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          color: 'var(--danger-color)',
          fontWeight: 500
        }}>
          <span>⚠️</span>
          <span>{validationError}</span>
          <button 
            onClick={() => setValidationError('')}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: 'none',
              color: 'var(--danger-color)',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >✕</button>
        </div>
      )}

      </div>

      {/* 5. ADVANCED FILTERS - Collapsible group */}
      <div className="filter-group">
        <button type="button" className="filter-group-head is-toggle" onClick={() => setShowLocation(!showLocation)} aria-expanded={showLocation}>
          <span className="fg-icon">📍</span>
          <span className="fg-title">Location &amp; Occupation</span>
          <span className="fg-sum">{`📍 ${(searchCriteria.locations || []).length ? searchCriteria.locations.join(', ') : (searchCriteria.state || 'Any')} · 💼 ${(searchCriteria.occupations || []).length ? searchCriteria.occupations.join(', ') : 'Any'}`}</span>
          {((searchCriteria.locations || []).length + (searchCriteria.state ? 1 : 0) + ((searchCriteria.occupations || []).length ? 1 : 0)) > 0 && <span className="fg-count">{(searchCriteria.locations || []).length + (searchCriteria.state ? 1 : 0) + ((searchCriteria.occupations || []).length ? 1 : 0)} active</span>}
          <span className="fg-chev">{showLocation ? '▲' : '▼'}</span>
        </button>
        {showLocation && (
          <div className="location-filters-section">
            <div className="col-location">
                      <div className="form-group">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          Location
                          <Tooltip 
                            text="Filter by city or state. Select multiple locations to expand your search."
                            position="top"
                            icon 
                          />
                        </label>
                        <LocationMultiSelect
                          options={locationOptions}
                          selected={searchCriteria.locations || []}
                          onChange={(selectedLocations) => {
                            // Update search criteria with selected locations
                            handleInputChange({
                              target: {
                                name: 'locations',
                                value: selectedLocations
                              }
                            });
                          }}
                          placeholder="Select locations..."
                          maxVisible={3}
                          disabled={isDirectLookupMode}
                        />
                      </div>
                    </div>
            <div className="col-state">
                      <div className="form-group">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          State
                          <Tooltip
                            text="Filter by state. Near Me can auto-fill this based on your current location."
                            position="top"
                            icon
                          />
                        </label>
                        <select
                          className="form-control"
                          name="state"
                          value={searchCriteria.state || ''}
                          onChange={handleInputChange}
                          disabled={isDirectLookupMode}
                        >
                          <option value="">Any State</option>
                          {US_STATES.map((stateOption) => (
                            <option key={stateOption} value={stateOption}>{stateOption}</option>
                          ))}
                        </select>
                      </div>
                    </div>
            <div className="col-occupation">
                      <div className="form-group">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          Occupation
                          <Tooltip 
                            text="Filter by profession or career field. Select multiple options."
                            position="top"
                            icon 
                          />
                        </label>
                        <OccupationMultiSelect
                          options={occupationOptions}
                          selected={searchCriteria.occupations || []}
                          onChange={(selected) => handleInputChange({ target: { name: 'occupations', value: selected } })}
                          placeholder="Select professions..."
                          disabled={isDirectLookupMode}
                        />
                      </div>
                    </div>
          </div>
        )}
      </div>

      <div className="filter-group">
        <button type="button" className="filter-group-head is-toggle" onClick={() => setShowAdvancedFilters(!showAdvancedFilters)} aria-expanded={showAdvancedFilters}>
          <span className="fg-icon">🔧</span>
          <span className="fg-title">Advanced</span>
          <span className="fg-sum">Body · Eating · Drinking · Smoking · ID · Keyword</span>
          {advActiveCount > 0 && <span className="fg-count">{advActiveCount} active</span>}
          <span className="fg-chev">{showAdvancedFilters ? '▲' : '▼'}</span>
        </button>
      {showAdvancedFilters && (
        <>
          <div className={`advanced-filters-section ${!hideActionButtons ? 'has-actions' : ''}`}>
              
              
              <div className="col-body-type">
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Body Type
                    <Tooltip 
                      text="Filter by body type preference if specified by the user."
                      position="top"
                      icon 
                    />
                  </label>
                  <select
                    className="form-control"
                    name="bodyType"
                    value={searchCriteria.bodyType || ''}
                    onChange={handleInputChange}
                    disabled={isDirectLookupMode}
                  >
                    <option value="">Any</option>
                    {bodyTypeOptions.map(option => option && (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="col-eating">
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Eating
                    <Tooltip 
                      text="Filter by eating preference (vegetarian, non-vegetarian, vegan, etc.)."
                      position="top"
                      icon 
                    />
                  </label>
                  <select
                    className="form-control"
                    name="eatingPreference"
                    value={searchCriteria.eatingPreference || ''}
                    onChange={handleInputChange}
                    disabled={isDirectLookupMode}
                  >
                    <option value="">Any</option>
                    {eatingOptions.map(option => option && (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-drinking">
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Drinking
                    <Tooltip 
                      text="Filter by alcohol consumption habits (never, socially, regularly, etc.)."
                      position="top"
                      icon 
                    />
                  </label>
                  <select
                    className="form-control"
                    name="drinking"
                    value={searchCriteria.drinking || ''}
                    onChange={handleInputChange}
                    disabled={isDirectLookupMode}
                  >
                    <option value="">Any</option>
                    {lifestyleOptions.map(option => option && (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-smoking">
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Smoking
                    <Tooltip 
                      text="Filter by smoking habits (never, occasionally, regularly, etc.)."
                      position="top"
                      icon 
                    />
                  </label>
                  <select
                    className="form-control"
                    name="smoking"
                    value={searchCriteria.smoking || ''}
                    onChange={handleInputChange}
                    disabled={isDirectLookupMode}
                  >
                    <option value="">Any</option>
                    {lifestyleOptions.map(option => option && (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* 0. PROFILE ID DIRECT SEARCH - Separate Section */}
              <div className="col-profile-id">
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Profile ID / Username
                    <Tooltip 
                      text="Enter a Profile ID or Username to find a specific user directly. This bypasses all other filters."
                      position="top"
                      icon 
                    />
                  </label>
                  <div className="profile-id-search-row">
                    <input
                      type="text"
                      className="form-control"
                      name="profileId"
                      id="profileId-input"
                      value={searchCriteria.profileId || ''}
                      onChange={handleInputChange}
                      placeholder="e.g., STHa9Lor or john_doe"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleMainSearch();
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="col-keyword">
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Keyword Search
                    <Tooltip 
                      text="Search across all profile fields including bio, interests, occupation, and more. Leave empty to see all matches."
                      position="top"
                      icon 
                    />
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="keyword"
                    value={searchCriteria.keyword || ''}
                    onChange={handleInputChange}
                    disabled={isDirectLookupMode}
                    placeholder="Search any field..."
                  />
                </div>
              </div>

              {!hideActionButtons && (
                <div className="col-advanced-actions">
                  {onSearch && (
                    <button
                      type="button"
                      onClick={handleMainSearch}
                      className="btn btn-primary"
                      title={searchButtonText}
                      aria-label={searchButtonText}
                    >
                      <span className="btn-icon" aria-hidden="true">🔍</span>
                    </button>
                  )}
                  {onNewMe && (
                    <button
                      type="button"
                      onClick={onNewMe}
                      className="btn btn-secondary"
                      title="Use your current city and run search"
                      aria-label="Near Me"
                      disabled={isNearMeLoading}
                    >
                      <span className="btn-icon" aria-hidden="true">{isNearMeLoading ? '⏳' : '📍'}</span>
                    </button>
                  )}
                  {onClear && (
                    <button
                      type="button"
                      onClick={onClear}
                      className={isAdmin ? 'btn btn-clear' : 'btn btn-primary'}
                      title={isAdmin ? 'Clear all filters (widest search)' : 'Reset to your partner preference defaults'}
                      aria-label={isAdmin ? 'Clear' : 'Reset'}
                    >
                      <span className="btn-icon" aria-hidden="true">{isAdmin ? '🗑️' : '🔄'}</span>
                    </button>
                  )}
                  {onSave && (
                    <button
                      type="button"
                      onClick={onSave}
                      className="btn btn-secondary"
                      title={saveButtonText}
                      aria-label={saveButtonText}
                    >
                      <span className="btn-icon" aria-hidden="true">💾</span>
                    </button>
                  )}
                </div>
              )}
          </div>
        </>
      )}
      </div>
    </div>
  );
};

export default SearchFilters;
